/**
 * SRZ Buy zone — product media gallery, product options (multi SKU) and
 * theme variant-picker integration.
 */
(function () {
  var VARIANT_UPDATE = 'variant:update';

  function formatMoney(cents) {
    var amount = Number(cents);
    if (!Number.isFinite(amount)) return '';
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(amount);
    }
    return '€' + (amount / 100).toFixed(2).replace('.', ',');
  }

  /**
   * Availability attributes are rendered as "true" / "false" strings; anything else
   * (missing attribute) is treated as available, matching the Liquid default.
   */
  function isAvailable(value) {
    return String(value) !== 'false';
  }

  /**
   * The product-level restriction the Liquid applied when it rendered this button
   * (`custom.set_as_out_of_stock`, `custom.hide_atc`). It does not depend on the
   * selected variant, so picking an available colour must never lift it.
   *
   * @param {Element} container - The <add-to-cart-component> wrapper.
   */
  function isPurchasable(container) {
    return isAvailable(container.getAttribute('data-wondr-srz-atc-purchasable'));
  }

  /**
   * Puts a container into the theme's own sold-out state and, when the product is
   * held back at product level, keeps it there.
   *
   * `product-form.js` calls `enable()` for every available variant without
   * consulting `data-wondr-srz-atc-purchasable`, so picking an in-stock colour on a
   * product rendered unavailable restores type=submit, name=add and data-cvg-atc:
   * the button becomes submittable again despite the server-side restriction. Its
   * call and ours sit in the same `variant:update` dispatch, so listener order
   * alone would decide which one writes last. The microtask re-applies the
   * restriction once that dispatch has finished — and before the browser can hand
   * over a click, so there is no window in which the button can be submitted. It is
   * deliberately skipped for a plain out-of-stock variant: there `product-form.js`
   * already agrees with us, and a late write could clobber a newer selection.
   *
   * @param {Element} container - The <add-to-cart-component> wrapper.
   */
  function enforceSoldOutState(container) {
    // Without the component API we cannot set the submit attributes the way
    // `product-form.js` does; the server-rendered state is then left as it is.
    if (typeof container.applySoldOutState !== 'function') return;
    container.applySoldOutState();
    if (isPurchasable(container)) return;

    queueMicrotask(function () {
      if (!container.isConnected || isPurchasable(container)) return;
      // Only when the submit state was actually restored, so that a pending
      // `disable()` from `#onVariantSelected` — which leaves the restriction
      // intact — is not lifted again here.
      var button = container.querySelector('button');
      if (!button || button.type !== 'submit') return;
      container.applySoldOutState();
    });
  }

  /**
   * Swaps the add-to-cart label and price row to match the stock state, and applies
   * the sold-out button state when the item cannot be added.
   *
   * `product-form.js` already applies the sold-out *attributes* (type=button, no
   * name, no data-cvg-atc) for an unavailable variant, but its morph — the part
   * that would swap in the server-rendered sold-out label — is carved out inside
   * `.wondr-srz-buy`. Without this the button keeps reading "In winkelwagen :
   * <price>" while it can no longer add anything. Both labels come from data
   * attributes so no copy lives in JS. The product-level restriction is the half
   * `product-form.js` does not know about, so that one is enforced here.
   *
   * @param {Element|null} container - The <add-to-cart-component> wrapper.
   * @param {boolean} available - Whether the selected variant is in stock.
   * @returns {boolean} Whether the variant can actually be added: in stock *and* not
   *   held back by the product-level restriction. A sellable form is not enabled
   *   here — the buy zone leaves that to `product-form.js` so the pending state of
   *   an unconfirmed selection is not lifted early.
   */
  function applyAtcAvailability(container, available) {
    if (!container) return false;
    var button = container.querySelector('button');
    if (!button) return false;

    var sellable = available && isPurchasable(container);
    var labelEl = button.querySelector('[data-wondr-srz-atc-label]');
    var label = sellable
      ? container.getAttribute('data-wondr-srz-atc-label-available')
      : container.getAttribute('data-wondr-srz-atc-label-sold-out');

    if (labelEl && label) labelEl.textContent = label;
    button.classList.toggle('is-sold-out', !sellable);
    if (!sellable) enforceSoldOutState(container);
    return sellable;
  }

  /**
   * Wires the thumb rail + slides of a single gallery. Each product option
   * renders its own gallery, so state is kept per gallery instead of per section.
   */
  function initGallery(gallery) {
    var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-wondr-srz-thumb]'));
    var slides = Array.prototype.slice.call(gallery.querySelectorAll('[data-wondr-srz-slide]'));
    var thumbsPrev = gallery.querySelector('[data-wondr-srz-thumbs-prev]');
    var thumbsNext = gallery.querySelector('[data-wondr-srz-thumbs-next]');
    var thumbsWindowSize = 5;
    var thumbsWindowStart = 0;

    function getActiveSlideIndex() {
      var activeThumb = gallery.querySelector('[data-wondr-srz-thumb].is-active');
      if (!activeThumb) return 0;
      var parsed = Number(activeThumb.getAttribute('data-index'));
      return Number.isFinite(parsed) ? parsed : 0;
    }

    function updateThumbNav() {
      if (!thumbs.length) return;
      var hasOverflow = thumbs.length > thumbsWindowSize;
      var activeIndex = getActiveSlideIndex();
      var lastIndex = thumbs.length - 1;

      if (thumbsPrev) {
        thumbsPrev.classList.toggle('is-hidden', !hasOverflow);
        thumbsPrev.disabled = !hasOverflow || activeIndex <= 0;
      }

      if (thumbsNext) {
        thumbsNext.classList.toggle('is-hidden', !hasOverflow);
        thumbsNext.disabled = !hasOverflow || activeIndex >= lastIndex;
      }
    }

    function renderThumbWindow() {
      if (!thumbs.length) return;
      var hasOverflow = thumbs.length > thumbsWindowSize;
      var maxStart = Math.max(0, thumbs.length - thumbsWindowSize);
      if (thumbsWindowStart > maxStart) thumbsWindowStart = maxStart;

      thumbs.forEach(function (thumb, index) {
        var inWindow = !hasOverflow || (index >= thumbsWindowStart && index < thumbsWindowStart + thumbsWindowSize);
        thumb.classList.toggle('is-hidden', !inWindow);
      });

      updateThumbNav();
    }

    function ensureThumbVisible(index) {
      if (!thumbs.length || thumbs.length <= thumbsWindowSize) return;
      var maxStart = Math.max(0, thumbs.length - thumbsWindowSize);
      if (index < thumbsWindowStart) {
        thumbsWindowStart = index;
      } else if (index >= thumbsWindowStart + thumbsWindowSize) {
        thumbsWindowStart = index - thumbsWindowSize + 1;
      }
      thumbsWindowStart = Math.max(0, Math.min(thumbsWindowStart, maxStart));
      renderThumbWindow();
    }

    function setActiveSlide(index) {
      var activeIndex = Number(index);
      thumbs.forEach(function (thumb) {
        var active = thumb.getAttribute('data-index') === String(index);
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      slides.forEach(function (slide) {
        var active = slide.getAttribute('data-index') === String(index);
        slide.classList.toggle('is-active', active);
      });
      if (Number.isFinite(activeIndex)) {
        ensureThumbVisible(activeIndex);
      }
      updateThumbNav();
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        setActiveSlide(thumb.getAttribute('data-index'));
      });
    });

    if (thumbsPrev) {
      thumbsPrev.addEventListener('click', function () {
        var currentIndex = getActiveSlideIndex();
        if (currentIndex <= 0) return;
        setActiveSlide(String(currentIndex - 1));
      });
    }

    if (thumbsNext) {
      thumbsNext.addEventListener('click', function () {
        var currentIndex = getActiveSlideIndex();
        var maxIndex = thumbs.length - 1;
        if (currentIndex >= maxIndex) return;
        setActiveSlide(String(currentIndex + 1));
      });
    }

    if (slides.length) {
      var hasActiveSlide = slides.some(function (slide) {
        return slide.classList.contains('is-active');
      });
      if (!hasActiveSlide) setActiveSlide('0');
    }

    renderThumbWindow();

    return { refresh: renderThumbWindow };
  }

  function initSection(section) {
    if (!section || section.dataset.wondrSrzBuyInit === 'true') return;
    section.dataset.wondrSrzBuyInit = 'true';

    var root = section.querySelector('[data-wondr-srz-buy-root]');
    if (!root) return;

    var salePillOverride = (section.getAttribute('data-sale-pill-override') || '').trim();
    var swatches = Array.prototype.slice.call(section.querySelectorAll('[data-wondr-srz-swatch]'));
    var optionButtons = Array.prototype.slice.call(section.querySelectorAll('[data-wondr-srz-option]'));
    var panels = Array.prototype.slice.call(section.querySelectorAll('[data-wondr-srz-panel]'));

    var galleries = Array.prototype.slice.call(section.querySelectorAll('[data-wondr-srz-gallery-panel]')).map(
      initGallery
    );

    /* ------------------------------------------------------------------ *
     * Product options — switch between different products (SKUs)
     * ------------------------------------------------------------------ */

    function activateOption(index) {
      var target = String(index);
      var background = '';
      var label = '';

      optionButtons.forEach(function (button) {
        var active = button.getAttribute('data-option-index') === target;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active) {
          background = button.getAttribute('data-background') || '';
          label = button.getAttribute('data-label') || '';
        }
      });

      panels.forEach(function (panel) {
        var active = panel.getAttribute('data-option-index') === target;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });

      var nameEl = section.querySelector('[data-wondr-srz-option-name]');
      if (nameEl) nameEl.textContent = label;

      if (background) {
        section.style.setProperty('--srz-buy-section-bg', background);
      } else {
        section.style.removeProperty('--srz-buy-section-bg');
      }

      // The newly visible gallery was hidden when its thumb window was measured.
      galleries.forEach(function (gallery) {
        gallery.refresh();
      });
    }

    optionButtons.forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        activateOption(button.getAttribute('data-option-index'));
      });
    });

    /* ------------------------------------------------------------------ *
     * Variant swatches — switch variants of a single product
     * ------------------------------------------------------------------ */

    function getPickerFor(productId) {
      var pickers = section.querySelectorAll('variant-picker');
      for (var i = 0; i < pickers.length; i++) {
        if (!productId || pickers[i].getAttribute('data-product-id') === String(productId)) {
          return pickers[i];
        }
      }
      return pickers[0] || null;
    }

    function setActiveSwatch(variantId) {
      if (!variantId) return;
      var colorName = section.querySelector('[data-wondr-srz-color-name]');
      swatches.forEach(function (sw) {
        var active = String(sw.getAttribute('data-variant-id')) === String(variantId);
        sw.classList.toggle('is-active', active);
        sw.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active && colorName) {
          colorName.textContent = sw.getAttribute('data-label') || colorName.textContent;
        }
      });
    }

    function selectVariantViaPicker(variantId) {
      var picker = getPickerFor(section.getAttribute('data-product-id'));
      if (!picker || !variantId) return;
      var input =
        picker.querySelector('input[type="radio"][data-variant-id="' + variantId + '"]') ||
        picker.querySelector('option[data-variant-id="' + variantId + '"]');
      if (!input) return;

      if (input.tagName === 'OPTION') {
        var select = input.parentElement;
        if (select && select.tagName === 'SELECT') {
          select.value = input.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }

      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * Availability of a variant according to the hidden picker — the control that
     * carries Shopify's own `data-option-available`.
     * @returns {boolean|null} null when the variant has no radio in the picker.
     */
    function availabilityFromPicker(variantId) {
      var picker = getPickerFor(section.getAttribute('data-product-id'));
      if (!picker || !variantId) return null;
      var input = picker.querySelector('input[type="radio"][data-variant-id="' + variantId + '"]');
      if (!input) return null;
      return isAvailable(input.getAttribute('data-option-available'));
    }

    swatches.forEach(function (sw) {
      sw.addEventListener('click', function (e) {
        e.preventDefault();
        var variantId = sw.getAttribute('data-variant-id');
        if (!variantId) return;
        setActiveSwatch(variantId);
        // Apply the swatch's own stock state right away: the picker needs a section
        // fetch before `variant:update` lands, and the button must not keep claiming
        // it can add in the meantime.
        applyAvailabilityToScopes(isAvailable(sw.getAttribute('data-available')));
        markLinkedFormsPending(variantId);
        selectVariantViaPicker(variantId);
      });
    });

    /* ------------------------------------------------------------------ *
     * Price / ATC sync
     * ------------------------------------------------------------------ */

    function getScopes(productId) {
      var scopes = Array.prototype.slice.call(
        section.querySelectorAll('[data-wondr-srz-product-scope="' + productId + '"]')
      );
      return scopes.length ? scopes : [section];
    }

    /** Applies a stock state to every add-to-cart button of the section product. */
    function applyAvailabilityToScopes(available) {
      getScopes(section.getAttribute('data-product-id')).forEach(function (scope) {
        applyAtcAvailability(scope.querySelector('[data-wondr-srz-atc]'), available);
      });
    }

    /** Variant the visitor last picked here, confirmed or not. @type {string|null} */
    var pendingVariantId = null;
    /** Last selection a `variant:update` confirmed, for re-syncing reloaded sections. */
    var lastLinkedSelection = null;

    function getLinkedCards(productId) {
      return Array.prototype.slice.call(
        document.querySelectorAll(
          '[data-wondr-srz-linked-form][data-wondr-srz-linked-product="' + productId + '"]'
        )
      );
    }

    /**
     * Takes linked forms out of service until the new selection is confirmed. They live
     * in their own `.shopify-section`, so `product-form.js` never disables them on this
     * picker's `variant:selected`; without this the final CTA keeps submitting the
     * previous colour while the section request is in flight — and indefinitely if that
     * request fails. `enable()` / `applySoldOutState()` clear it again on confirmation.
     */
    function markLinkedFormsPending(variantId) {
      pendingVariantId = String(variantId);
      getLinkedCards(section.getAttribute('data-product-id')).forEach(function (card) {
        var container = card.querySelector('[data-wondr-srz-atc]');
        if (!container || typeof container.disable !== 'function') return;
        container.disable();
      });
    }

    /**
     * Keeps add-to-cart forms in *other* sections (the final CTA) on the variant the
     * visitor picked here. Those sections render their own `<product-form-component>`,
     * which only listens for `variant:update` inside its own `.shopify-section`, so it
     * never sees this one and would otherwise always add the variant it was rendered
     * with. Sold-out handling reuses `add-to-cart-component`'s own methods so the
     * submit attributes and the `data-cvg-atc` Converge hook stay in step with
     * `product-form.js` instead of being re-implemented here.
     */
    function syncLinkedForms(resource, productId) {
      var cards = getLinkedCards(productId);
      if (!cards.length) return;

      // A superseded or late section response must not hand the form back for a colour
      // the visitor has already moved away from. Only this section's own product goes
      // through the swatches, so only its pending selection can be stale.
      var isSectionProduct = String(productId) === String(section.getAttribute('data-product-id'));
      if (isSectionProduct && pendingVariantId !== null && String(resource.id) !== pendingVariantId) {
        return;
      }

      var available = resource.available !== false;
      var label = '';
      var activeSwatch = section.querySelector('[data-wondr-srz-swatch].is-active');
      if (activeSwatch) label = activeSwatch.getAttribute('data-label') || '';

      cards.forEach(function (card) {
        var container = card.querySelector('[data-wondr-srz-atc]');
        // Without the component API we cannot restore submit/sold-out attributes, so
        // leave the form exactly as the server rendered it rather than binding it to a
        // variant it may not be able to add.
        if (!container || typeof container.enable !== 'function') return;

        var variantInput = card.querySelector('input[name="id"]');
        if (variantInput) variantInput.value = String(resource.id);

        var button = container.querySelector('button');
        if (button) {
          button.dataset.variantId = String(resource.id);
          button.dataset.price = (resource.price / 100).toFixed(2);
        }

        // Variant stock alone is not enough: this section renders its own
        // `can_add_starter` guard from the product metafields, and enable() would
        // otherwise restore submit + data-cvg-atc on a product held back on purpose.
        // The sold-out branch is applied by applyAtcAvailability itself; only a
        // sellable form has to be handed back here, since nothing else re-enables a
        // form in this section.
        if (applyAtcAvailability(container, available)) container.enable();

        var colorEl = card.querySelector('[data-wondr-srz-linked-color]');
        if (colorEl && label) colorEl.textContent = label;
      });

      lastLinkedSelection = { resource: resource, productId: productId };
    }

    /**
     * Re-applies the last confirmed selection to linked sections that were re-rendered
     * after it was made. The theme editor reloads one section at a time, so the final
     * CTA comes back server-rendered with its default colour while the buy zone still
     * shows the visitor's choice.
     */
    function resyncLinkedForms(target) {
      if (!lastLinkedSelection || !target || typeof target.querySelector !== 'function') return;
      if (target.contains(section)) return;
      var linkedSelector =
        '[data-wondr-srz-linked-form][data-wondr-srz-linked-product="' + lastLinkedSelection.productId + '"]';
      if (!target.querySelector(linkedSelector)) return;
      syncLinkedForms(lastLinkedSelection.resource, lastLinkedSelection.productId);
    }

    function onLinkedSectionLoad(event) {
      // The buy zone itself may have been replaced; that copy re-initialises on its own
      // and this listener belongs to the old element.
      if (!document.contains(section)) {
        teardown();
        return;
      }
      resyncLinkedForms(event.target);
    }

    function onSectionUnload(event) {
      if (!event.target || typeof event.target.contains !== 'function') return;
      if (!event.target.contains(section)) return;
      teardown();
    }

    function teardown() {
      document.removeEventListener('shopify:section:load', onLinkedSectionLoad);
      document.removeEventListener('shopify:section:unload', onSectionUnload);
    }

    function updatePriceFromVariant(resource, productId) {
      if (!resource) return;

      var price = resource.price;
      var compare = resource.compare_at_price;
      var hasDiscount = compare && compare > price;

      getScopes(productId).forEach(function (scope) {
        var priceEl = scope.querySelector('[data-wondr-srz-price]');
        var compareEl = scope.querySelector('[data-wondr-srz-compare]');
        var salePillEl = scope.querySelector('[data-wondr-srz-sale-pill]');
        var atcCurrentEl = scope.querySelector('[data-wondr-srz-atc-current]');

        if (priceEl) priceEl.textContent = formatMoney(price);

        if (compareEl) {
          if (hasDiscount) {
            compareEl.textContent = formatMoney(compare);
            compareEl.classList.remove('wondr-srz-buy__compare--hidden');
          } else {
            compareEl.classList.add('wondr-srz-buy__compare--hidden');
          }
        }

        if (salePillEl) {
          if (salePillOverride) {
            salePillEl.textContent = salePillOverride;
            salePillEl.classList.remove('wondr-srz-buy__sale-pill--hidden');
          } else if (hasDiscount) {
            var pct = Math.round(((compare - price) / compare) * 100);
            salePillEl.textContent = '−' + pct + '%';
            salePillEl.classList.remove('wondr-srz-buy__sale-pill--hidden');
          } else {
            salePillEl.classList.add('wondr-srz-buy__sale-pill--hidden');
          }
        }

        if (atcCurrentEl) atcCurrentEl.textContent = formatMoney(price);

        var atcBtn = scope.querySelector('[data-cvg-atc]');
        if (atcBtn && resource.id) {
          atcBtn.dataset.variantId = String(resource.id);
          atcBtn.dataset.price = (price / 100).toFixed(2);
        }
      });
    }

    function onVariantUpdate(event) {
      var productId = event.detail?.data?.productId;
      if (!productId) return;

      var resource = event.detail.resource;
      if (!resource) return;

      var isSectionProduct = String(productId) === String(section.getAttribute('data-product-id'));
      if (isSectionProduct) {
        setActiveSwatch(resource.id);
      }

      // Same input `product-form.js` uses for applySoldOutState(), so label and
      // button state cannot disagree. The hidden picker is the fallback when the
      // section response carries no `available` flag.
      var available = resource.available;
      if (typeof available !== 'boolean') {
        var fromPicker = isSectionProduct ? availabilityFromPicker(resource.id) : null;
        available = fromPicker === null ? true : fromPicker;
      }

      getScopes(productId).forEach(function (scope) {
        applyAtcAvailability(scope.querySelector('[data-wondr-srz-atc]'), available);
      });

      updatePriceFromVariant(resource, productId);
      requestAnimationFrame(function () {
        updatePriceFromVariant(resource, productId);
      });
      syncLinkedForms(resource, productId);
    }

    section.addEventListener(VARIANT_UPDATE, onVariantUpdate);
    document.addEventListener('shopify:section:load', onLinkedSectionLoad);
    document.addEventListener('shopify:section:unload', onSectionUnload);

    if (optionButtons.length) {
      var initialButton =
        section.querySelector('[data-wondr-srz-option].is-active') || optionButtons[0];
      activateOption(initialButton.getAttribute('data-option-index'));
    }

    var initialVariantInput = section.querySelector('input[name="id"]');
    if (initialVariantInput && initialVariantInput.value) {
      setActiveSwatch(initialVariantInput.value);
    }

    if (window.WondrSrz) window.WondrSrz.initFadeUp(section);
  }

  function boot() {
    document.querySelectorAll('[id^="wondr-srz-buy-"]').forEach(initSection);
  }

  document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('[id^="wondr-srz-buy-"]');
    if (el) initSection(el);
    if (e.target.id && e.target.id.indexOf('wondr-srz-buy-') === 0) initSection(e.target);
  });
})();
